import { Button, CloseButton } from '@ui/shared/Button';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import styled from 'styled-components';
import {
  activeMetrics,
  IMetrics,
} from '../../../data/providers/metricsProvider';
import { entityTestsBySession } from '../../../data/providers/testsProvider';
import { Entity, EntityType, IMetric, ITest } from '../../../data/types';
import { useRepetitions } from '../../../hooks/repetitions/useRepetitions';
import { fullDate } from '../../../utils/date';
import { toActiveMetricList } from '../../../utils/testMetrics';
import { FullHeightColumn } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ForceTime } from '../../shared/graphs/ForceTime';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { SectionHeader } from '../SectionHeader';
import { Scoring } from '../test/scoring/Scoring';
import { SessionTestList } from './SessionTestList';

interface IPopupProps {
  onClose: () => void;
  sessionId: number;
  entityType: EntityType;
  entity: Entity;
}

export const SessionPopup = (props: IPopupProps): React.ReactElement => {
  const [feedData, setFeedData] = React.useState<ITest[]>([]);
  const [feedLoading, setFeedLoading] = React.useState<boolean>(true);
  const [selectedTest, setSelectedTest] = React.useState<string>();
  const [activeMetricMap, setActiveMetricMap] = React.useState<IMetrics>();

  /**
   * @description subscribe to active metrics
   */
  React.useEffect((): (() => void) => {
    const subscriber = activeMetrics.subscribe((mMetrics: IMetrics): void =>
      setActiveMetricMap(mMetrics)
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  React.useEffect((): (() => void) => {
    const testsSubscriber = entityTestsBySession(
      props.entity.id,
      props.entityType,
      props.sessionId
    ).subscribe((mTests: ITest[]): void => {
      unstable_batchedUpdates((): void => {
        setFeedData(mTests);
        setFeedLoading(false);
      });
    });
    return (): void => testsSubscriber.unsubscribe();
  }, []);

  const currentTest = React.useMemo((): ITest => {
    return selectedTest
      ? feedData.find((item: ITest): boolean => item.id === selectedTest)
      : feedData[0];
  }, [feedLoading, selectedTest]);

  const { repetitions, selectedRepetition, selectedRepetitionChangeStream } =
    useRepetitions(currentTest?.id);

  const metrics = React.useMemo((): IMetric[] => {
    if (feedLoading) return [];
    if (!currentTest) return [];

    let mMetrics: IMetric[];
    if (selectedRepetition !== null) {
      mMetrics = sortBy(
        selectedRepetition.metrics,
        (metric: IMetric): number => metric.order
      ).filter((value: IMetric): boolean =>
        Boolean(activeMetricMap[value?.id])
      );
    } else {
      mMetrics = toActiveMetricList(
        currentTest,
        activeMetricMap[currentTest.testType.id]
      );
    }
    return mMetrics;
  }, [JSON.stringify(currentTest), JSON.stringify(selectedRepetition)]);

  return (
    <PopupCard onClose={props.onClose} size='full' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft style={{ marginLeft: '15px' }}>
            <SectionHeader
              entityName={props.entity.name}
              entityImage={props.entity.image}
              secondaryTextMajor='Session'
              secondaryTextMinor={fullDate(props.sessionId)}
            />
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody style={{ backgroundColor: 'var(--color-surface-0)' }}>
          <Row shouldCollapse>
            <LeftSide>
              <SessionTestList
                selectTest={setSelectedTest}
                selectedTest={selectedTest}
                data={feedData}
                loading={feedLoading}
                sessionId={props.sessionId}
                entity={props.entity}
                entityType={props.entityType}
              />
            </LeftSide>
            <RightSide>
              <ForceTime
                test={currentTest}
                selectedRepetitionChangeStream={selectedRepetitionChangeStream}
                repetitions={repetitions}
                selectedRepetition={selectedRepetition}
              />
              <Scoring test={currentTest} metrics={metrics} level={1} />
            </RightSide>
          </Row>
        </CardBody>
      </PopupCardContent>
    </PopupCard>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LeftSide = styled(FullHeightColumn as any)`
  flex: 20;
  padding-bottom: var(--grid-gap);
  padding-left: var(--grid-gap);
  padding-right: var(--grid-gap);
  @media (max-width: 990px) {
    order: 2;
    padding-left: 5px;
    padding-right: 0px;
  }
`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RightSide = styled(FullHeightColumn as any)`
  flex: 80;
  padding-right: var(--grid-gap);
  padding-bottom: var(--grid-gap);
  @media (max-width: 990px) {
    order: 1;
    padding-left: 5px;
    padding-right: 0px;
    padding-bottom: 0px;
  }
`;
